import { Check, ShieldCheck, Award, Hammer, Clock } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import useReveal from "@/hooks/useReveal";
import { useSiteAssets } from "@/context/SiteAssetsContext";

const ICONS = [Hammer, Award, Clock, ShieldCheck];

export default function About() {
  const { t } = useLang();
  const { ref, visible } = useReveal();
  const { images } = useSiteAssets();
  const points = t("about.points");

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section
      id="about"
      data-testid="about-section"
      className="py-20 md:py-28 lg:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div ref={ref} className={`reveal ${visible ? "visible" : ""} grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-start`}>
          {/* Left: Text */}
          <div className="lg:col-span-7">
            <div className="flex items-center gap-3 mb-4">
              <span className="h-[2px] w-10 bg-[#E11D2E]" />
              <span className="text-xs uppercase tracking-[0.22em] text-neutral-600 font-semibold">
                {t("about.eyebrow")}
              </span>
            </div>
            <h2 className="font-display font-black text-neutral-900 text-3xl sm:text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.03]">
              {t("about.title")}
            </h2>

            <div className="mt-8 space-y-5 text-base md:text-lg text-neutral-700 leading-relaxed max-w-2xl">
              <p>{t("about.p1")}</p>
              <p>{t("about.p2")}</p>
            </div>

            <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 gap-4">
              {Array.isArray(points) &&
                points.map((p, i) => {
                  const Icon = ICONS[i % ICONS.length];
                  return (
                    <div
                      key={p}
                      data-testid={`about-point-${i}`}
                      className="flex items-start gap-4 p-5 bg-[#F8F9FA] border-l-2 border-[#1F9D40]"
                    >
                      <div className="w-10 h-10 rounded-full bg-white border border-neutral-200 flex items-center justify-center shrink-0">
                        <Icon size={18} className="text-[#1F9D40]" strokeWidth={2.2} />
                      </div>
                      <div>
                        <div className="font-display font-bold text-neutral-900 text-sm md:text-base">
                          {p}
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>

            <button
              data-testid="about-cta"
              onClick={() => scrollTo("contact")}
              className="mt-10 inline-flex items-center gap-2 border-2 border-neutral-900 hover:bg-neutral-900 hover:text-white text-neutral-900 font-bold px-7 py-4 rounded-full text-sm transition-all"
            >
              {t("about.cta")} →
            </button>
          </div>

          {/* Right: Visual */}
          <div className="lg:col-span-5 relative">
            <div className="relative overflow-hidden">
              <img
                src={images.about_team}
                alt="Equipo Modolo Multiservices"
                className="w-full aspect-[4/5] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
            </div>
            {/* Floating stats card */}
            <div className="absolute -bottom-6 -left-4 md:-left-8 bg-white border border-neutral-200 p-5 md:p-6 max-w-[260px] shadow-xl">
              <div className="flex items-baseline gap-2">
                <span className="font-display font-black text-4xl md:text-5xl text-neutral-900">32</span>
                <span className="font-display font-bold text-2xl text-[#E11D2E]">+</span>
              </div>
              <div className="text-xs uppercase tracking-widest text-neutral-500 mt-1">
                {t("trust.years_label")}
              </div>
              <div className="mt-4 flex items-center gap-2 text-xs text-neutral-700">
                <Check size={14} className="text-[#1F9D40]" strokeWidth={3} />
                {t("trust.materials_label")}
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
